<template>
<div
    v-if="bulk_message != null"
>

    <template
        v-if="bulk_message.filter_profile.before_send_add_list_ids.visible"
    >
        <h2
            style="margin-bottom: 15px"
        >
            Before Sending
        </h2>

        <v-alert
            border="left"
            color="primary"
            class="shaded-light"
            colored-border
            type="error"
            elevation="0"
        >
            This feature has been relocated.
            To add recipients from a list, go to the Recipients menu and select “Add Recipients from a List…”
            <br/>
            <br/>
            To proceed with your bulk message, please <a @click="clearBeforeSendAddListIds">clear</a> any selections in the 'Before sending, create…' field below.
        </v-alert>

        <m-edit-text
            :label="bulk_message.filter_profile.before_send_add_list_ids.label"
            :value="bulk_message.filter_profile.before_send_add_list_ids.value_description"
            :read_only="true"
            v-show="bulk_message.filter_profile.before_send_add_list_ids.visible"
            style="margin-left: 15px"
        />
    </template>
    <template
        v-else
    >
        <v-alert
            border="left"
            color="secondary"
            colored-border
            type="info"
            elevation="0"
            class="shaded-light mt-4"
        >
            Looking for <span class="font-weight-medium">Before Sending</span>? This feature has been relocated.
            To add recipients from a list, go to the Recipients menu and select “Add Recipients from a List…”
        </v-alert>
    </template>

    <h2 class="mt-12 mb-4">
        When Preparing Message, Remove Recipients Based On...
    </h2>

    <v-row>
        <v-col
            cols="12"
            md="10"
            lg="8"
            xl="7"
        >
            <div class="shaded-light py-5 px-6">
                <h3 class="mb-4">Other Messages</h3>

                <m-edit-text
                    v-show="bulk_message.filter_profile.filter_days_since_last_bulk_message.visible"
                    :label="bulk_message.filter_profile.filter_days_since_last_bulk_message.label"
                    :value="bulk_message.filter_profile.filter_days_since_last_bulk_message.value_description"
                    :custom_on_edit="onEditFilterDaysSince"
                    :read_only="has_sent"
                />

                <m-edit-text
                    v-show="bulk_message.filter_profile.filter_recipients_in_other_bulk_messages.visible"
                    :label="bulk_message.filter_profile.filter_recipients_in_other_bulk_messages.label"
                    :value="bulk_message.filter_profile.filter_recipients_in_other_bulk_messages.value_description"
                    :custom_on_edit="onEditFilterOtherBulkMessages"
                    :read_only="has_sent"
                />
            </div>
        </v-col>
    </v-row>

    <v-row>
        <v-col
            cols="12"
            md="10"
            lg="8"
            xl="7"
        >
            <div class="shaded-light py-5 px-6">
                <h3 class="mb-4">Consent and/or Purchase History<sup>1</sup></h3>

                <m-edit-text
                    v-show="bulk_message.filter_profile.filter_order_history.visible"
                    :label="bulk_message.filter_profile.filter_order_history.label"
                    :value="bulk_message.filter_profile.filter_order_history.value_description"
                    :custom_on_edit="onEditFilterOrderHistory"
                    :read_only="has_sent"
                />

                <m-edit-text
                    v-show="bulk_message.filter_profile.filter_consent.visible"
                    :label="bulk_message.filter_profile.filter_consent.label"
                    :value="bulk_message.filter_profile.filter_consent.value_description"
                    :custom_on_edit="onEditFilterConsent"
                    :read_only="has_sent"
                />

                <v-checkbox
                    v-show="bulk_message.filter_profile.filter_twilio_carrier_deactivation_report.visible"
                    :label="bulk_message.filter_profile.filter_twilio_carrier_deactivation_report.label"
                    v-model="bulk_message.filter_profile.filter_twilio_carrier_deactivation_report.value"
                    :readonly="has_sent || !bulk_message.filter_profile.filter_twilio_carrier_deactivation_report_can_edit"
                    :disabled="has_sent || !bulk_message.filter_profile.filter_twilio_carrier_deactivation_report_can_edit"
                    class="align-center mb-3 mt-0"
                    hide-details
                    @change="onSetTwilioCarrierDeactivationReportsFilter"
                >
                    <template #append>
                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    href="https://redchirp.com/removing-recipients-using-twilio-carrier-deactivation-reports/"
                                    target="_blank"
                                    icon
                                >
                                    <v-icon>mdi-information-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>Learn more: Removing Recipients Using Twilio Carrier Deactivation Reports</span>
                        </v-tooltip>
                    </template>
                </v-checkbox>

                <div
                    v-if="bulk_message.filter_profile.filter_twilio_carrier_deactivation_report.value"
                    class="ml-8"
                    style="max-width: 570px"
                >
                    <v-text-field
                        v-model="bulk_message.override_number_reassignment_check_since_date_look_back_days"
                        label="(Optional) Override the number of days to look back for the Number Reassignment Check Since Date"
                        :placeholder="bulk_message.filter_profile.default_number_reassignment_check_since_date_look_back_days?.toString()"
                        persistent-placeholder
                        hide-details
                        clearable
                        @change="onSetOverrideNumberReassignmentCheckSinceDateLookBackDays"
                    />
                </div>

                <p
                    class="mt-6 mb-0"
                    style="font-size: 12px;"
                >
                    1 &mdash; RedChirp automatically tries to avoid sending bulk messages to recipients who have
                    opted out, and to recipients who appear in tcpalitigatorlist.com (which advertises itself as a
                    database of known TCPA litigators, TCPA trolls, and DNC Complainers)
                </p>
            </div>
        </v-col>
    </v-row>

    <v-row>
        <v-col
            cols="12"
            md="10"
            lg="8"
            xl="7"
        >
            <div class="shaded-light py-5 px-6">
                <h3 class="mb-2">State-Specific Checks</h3>

                <p class="mb-0">
                    <rc-link
                        text="Click here for more information about Texas S.B. 140"
                        href="https://redchirp.com/questions-about-the-new-texas-law-s-b-140/"
                    />
                </p>

                <v-checkbox
                    v-show="bulk_message.filter_profile.filter_texas_do_not_call_list.visible"
                    :label="bulk_message.filter_profile.filter_texas_do_not_call_list.label"
                    v-model="bulk_message.filter_profile.filter_texas_do_not_call_list.value"
                    :readonly="has_sent"
                    :disabled="has_sent"
                    hide-details
                    @change="onSetTexasDoNotCallListFilter"
                />

                <!-- If more states are added to the potential exclusions, then this should be a multi select and the label should come from the backend -->
                <v-checkbox
                    v-show="bulk_message.filter_profile.filter_recipients_in_locations.visible"
                    label="Try to remove recipients who either (1) have a phone number with a Texas area code or (2) have an address in Texas. Removal is best effort and may be imperfect."
                    :input-value="bulk_message.filter_profile.filter_recipients_in_locations.value.length != 0"
                    :readonly="has_sent"
                    :disabled="has_sent"
                    class="mb-3"
                    hide-details
                    @change="onSetExcludedLocationsFilter"
                />
            </div>
        </v-col>
    </v-row>

    <p class="mt-4">
        <v-btn
            color="primary"
            plain
            @click="onClickTechnicalDetails"
        >
            Technical Details
        </v-btn>
    </p>
</div>
</template>

<script>
export default {
    props: {
        bulk_message: {
            type: Object,
            default: function() { return null; }
        },
        has_sent: {
            type: Boolean,
            default: false,
        },
    },

    methods: {
        onEditFilterOrderHistory()
        {
            this.$rc.prompt({
                    'title' : 'Order history filter settings',
                    'label' : 'Only send to recipients who have consent on file in RedChirp OR have purchased within the last (number of months)',
                    'value' : this.bulk_message.filter_order_history,
                    'unset_label': 'Do not use this filter',
                    'is_valid': this.$rc.validationRuleInteger(1,null),
                    'after_prompt' : (value) => {
                        
                        this.$emit(
                            'patch',
                            {
                                'filter_order_history': value,
                            },
                        );

                    }
                });
        },

        onEditFilterDaysSince() {
            
            this.$rc.prompt({
                    'title' : 'Recent bulk message filter settings',
                    'label' : this.bulk_message.filter_profile.filter_days_since_last_bulk_message.label,
                    'value' : this.bulk_message.filter_days_since_last_bulk_message,
                    'unset_label': 'Do not use this filter',
                    'is_valid': this.$rc.validationRuleInteger(1,null),
                    'after_prompt' : (value) => {
                        
                        this.$emit(
                            'patch',
                            {
                                'filter_days_since_last_bulk_message': value,
                            },
                        );

                    }
                });
        },

        onEditFilterOtherBulkMessages()
        {
            this.$rc.pick({
                'title': 'Do not send to recipients of other bulk message(s) filter settings',
                'content_before': '',
                'content_after': null,
            
                'choices': [],
            
                'multiple': true,
                'type': 'select_object_type',
                'select_object_type': 'bulk_message',
                'select_label': this.bulk_message.filter_profile.filter_recipients_in_other_bulk_messages.label,
                'initial_value': this.bulk_message.filter_recipients_in_other_bulk_messages,
                'unset_label': 'Do not use this fitler',
            
                'on_ok': (value) => {

                    if ( value == null ) 
                        value = [];
                    
                    
                    this.$emit(
                            'patch',
                            {
                                'filter_recipients_in_other_bulk_messages': value,
                            },
                        );
                },
            
                'on_cancel': () => {}
            
            });
        },

        onEditFilterConsent()
        {
            

            this.$rc.pick({
                'title': 'Consent filter settings',
                'content_before': '',
                'content_after': null,
            
                'choices': this.$rc.enumValues('consent_filter_type', false),
            
                'multiple': false,
                'type': 'select',
                'select_label': 'Choose One',
                'initial_value': this.bulk_message.filter_profile.filter_consent.value ?? 'none',
                'unset_label': 'Do not use this filter',
            
                'on_ok': (value) => {
                    if ( value == null ) value = 'none';

                    this.$emit(
                            'patch',
                            {
                                'filter_consent': value,
                            },
                        );
                },
            
                'on_cancel': () => {}
            
            });
        },

        onSetTexasDoNotCallListFilter(value)
        {
            this.$emit(
                'patch',
                {
                    'filter_texas_do_not_call_list': value,
                },
            );
        },

        onSetExcludedLocationsFilter(value)
        {
            const list_value = value ? ['US-TX'] : [];

            this.$emit(
                'patch',
                {
                    'filter_recipients_in_locations': list_value,
                },
            );
        },

        onSetTwilioCarrierDeactivationReportsFilter(value)
        {
            this.$emit(
                'patch',
                {
                    'filter_twilio_carrier_deactivation_report': value,
                },
            );
        },

        onSetOverrideNumberReassignmentCheckSinceDateLookBackDays(value)
        {
            this.$emit(
                'patch',
                {
                    'override_number_reassignment_check_since_date_look_back_days': value,
                },
            );
        },        

        onClickTechnicalDetails()
        {
            this.$rc.technicalDetails({
                'title': 'Filter Technical Details',
                'technical_details': this.bulk_message.filter_profile.technical_details,
            });
        },

        clearBeforeSendAddListIds()
        {
            this.$rc.confirm({
                title: 'Clear Lists',
                body: 'Are you ready to clear all "before sending" lists from this bulk message?',
                btn_text_ok: 'Yes, Clear Lists',
                btn_text_cancel: 'Not Yet',
                on_ok: () => {
                    this.$emit(
                        'patch',
                        {
                            'before_send_add_list_ids': [],
                        },
                    );
                }
            });
        },
    },
}
</script>

<style lang="scss" scoped>

</style>